import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Tooltip,
  Paper,
  Avatar,
  IconButton,
  Dialog,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from "@mui/icons-material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
  ReferenceArea,
  Tooltip as ChartTooltip,
} from "recharts";
import { getBrandLogoUrl } from "../../utils/brandLogoUtils";
import { getMetricLabel, getMetricTooltip } from "../../constants";

interface Snapshot {
  _id: string;
  timestamp: string;
  concurrentViewers: number;
  likeCount: number;
}

interface BrandMention {
  brandName: string;
  mentionStartTime: number;
  mentionType: "paid" | "organic" | "neutral";
  context?: string;
  s3Key?: string;
  logoUrl?: string;
}

interface Summary {
  startSeconds: number;
  endSeconds: number;
  summary: string;
}

interface StoryTimelineBarProps {
  totalDuration: number; // in seconds
  brandMentions: BrandMention[];
  peakViewersTime?: number; // in seconds
  videoId?: string;
  snapshots?: Snapshot[];
  actualStartTime?: string;
  summaries?: Summary[];
  onSegmentHover?: (segment: Summary | null) => void;
  timelineSegments?: Array<{
    startSeconds: number;
    endSeconds: number;
    viewers?: number;
    likes?: number;
  }>;
  editOffset?: number; // Time in seconds removed from the beginning of the YouTube video
  averageViewers?: number | null;
  peakViewers?: number | null;
  chatMessageCount?: number | null;
  uniqueChatUsers?: number | null;
}

const LivestreamStatPill: React.FC<{
  label: string;
  tooltip: string;
  formattedValue: string;
  bgcolor: string;
}> = ({ label, tooltip, formattedValue, bgcolor }) => (
  <Tooltip
    title={tooltip}
    arrow
    placement="top"
    componentsProps={{
      tooltip: {
        sx: {
          textAlign: "center",
          maxWidth: 280,
        },
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor,
        cursor: "default",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          fontWeight: 600,
          textAlign: "center",
          lineHeight: 1.2,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          color: "primary.main",
          textAlign: "center",
          lineHeight: 1.2,
        }}
      >
        {formattedValue}
      </Typography>
    </Box>
  </Tooltip>
);

const StoryTimelineBar: React.FC<StoryTimelineBarProps> = ({
  totalDuration,
  brandMentions,
  peakViewersTime,
  videoId,
  snapshots = [],
  actualStartTime,
  summaries = [],
  onSegmentHover,
  timelineSegments = [],
  editOffset = 0,
  averageViewers,
  peakViewers,
  chatMessageCount,
  uniqueChatUsers,
}) => {
  const theme = useTheme();
  const chartLine = theme.palette.primary.main;
  const chartAxisTickFill = theme.palette.text.secondary;
  const mentionLineColor = theme.palette.text.secondary;
  const chartPlotBg = "surfaceMuted";
  const statPillBg = "surfaceMuted";
  const dotStrokeColor = theme.palette.background.paper;
  const svgMutedTextFill = theme.palette.common.white;
  const chartGradientOverlay = `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`;
  const legendViewerSwatchBg = alpha(theme.palette.primary.main, 0.25);

  const [isFullscreen, setIsFullscreen] = useState(false);
  // Prepare chart data from snapshots - must be before any conditional returns
  const chartData = useMemo(() => {
    if (!snapshots.length || totalDuration <= 0) return [];

    const startTime = actualStartTime
      ? new Date(actualStartTime).getTime()
      : new Date(snapshots[0].timestamp).getTime();

    return (
      snapshots
        .map((s) => {
          const snapshotTime = new Date(s.timestamp).getTime();
          const offsetSeconds = Math.floor((snapshotTime - startTime) / 1000);

          return {
            offsetSeconds,
            viewers: s.concurrentViewers,
            likes: s.likeCount,
            position:
              totalDuration > 0 ? (offsetSeconds / totalDuration) * 100 : 0,
          };
        })
        // Filter to only include data within the video duration
        .filter((d) => d.offsetSeconds >= 0 && d.offsetSeconds <= totalDuration)
    );
  }, [snapshots, actualStartTime, totalDuration]);

  // Calculate peak viewers position - must be before any conditional returns
  // Only consider data strictly within the live duration
  const peakData = useMemo(() => {
    if (!chartData.length || totalDuration <= 0) return null;

    // Filter to only data within the actual live duration
    const liveData = chartData.filter(
      (d) => d.offsetSeconds >= 0 && d.offsetSeconds <= totalDuration,
    );

    if (!liveData.length) return null;

    const peak = liveData.reduce(
      (max, curr) => (curr.viewers > max.viewers ? curr : max),
      liveData[0],
    );

    // Double-check peak is within bounds
    if (peak.offsetSeconds < 0 || peak.offsetSeconds > totalDuration) {
      return null;
    }

    return peak;
  }, [chartData, totalDuration]);

  // Calculate 10-minute segments with most viewer gains and losses
  const viewerChangeSegments = useMemo(() => {
    // Helper to find summaries that overlap with a time range
    const findSummariesInRange = (
      startSeconds: number,
      endSeconds: number,
    ): Summary[] => {
      return summaries.filter(
        (s) =>
          (s.startSeconds >= startSeconds && s.startSeconds < endSeconds) ||
          (s.endSeconds > startSeconds && s.endSeconds <= endSeconds) ||
          (s.startSeconds <= startSeconds && s.endSeconds >= endSeconds),
      );
    };

    if (chartData.length < 2)
      return {
        bestGain: null,
        worstLoss: null,
        bestGainSummaries: [],
        worstLossSummaries: [],
      };

    const SEGMENT_DURATION = 600; // 10 minutes in seconds
    const segments: Array<{
      startSeconds: number;
      endSeconds: number;
      startViewers: number;
      endViewers: number;
      change: number;
      changePercent: number;
    }> = [];

    // Group data into 10-minute segments
    for (
      let segmentStart = 0;
      segmentStart < totalDuration;
      segmentStart += SEGMENT_DURATION
    ) {
      const segmentEnd = Math.min(
        segmentStart + SEGMENT_DURATION,
        totalDuration,
      );

      // Find data points in this segment
      const segmentData = chartData.filter(
        (d) => d.offsetSeconds >= segmentStart && d.offsetSeconds < segmentEnd,
      );

      if (segmentData.length >= 2) {
        const startViewers = segmentData[0].viewers;
        const endViewers = segmentData[segmentData.length - 1].viewers;
        const change = endViewers - startViewers;
        const changePercent =
          startViewers > 0 ? (change / startViewers) * 100 : 0;

        segments.push({
          startSeconds: segmentStart,
          endSeconds: segmentEnd,
          startViewers,
          endViewers,
          change,
          changePercent,
        });
      }
    }

    if (segments.length === 0)
      return {
        bestGain: null,
        worstLoss: null,
        bestGainSummaries: [],
        worstLossSummaries: [],
      };

    // Find best gain and worst loss
    const bestGainSegment = segments.reduce(
      (best, curr) => (curr.change > best.change ? curr : best),
      segments[0],
    );

    const worstLossSegment = segments.reduce(
      (worst, curr) => (curr.change < worst.change ? curr : worst),
      segments[0],
    );

    const bestGain = bestGainSegment.change > 0 ? bestGainSegment : null;
    const worstLoss = worstLossSegment.change < 0 ? worstLossSegment : null;

    // Pre-calculate summaries for each segment
    const bestGainSummaries = bestGain
      ? findSummariesInRange(bestGain.startSeconds, bestGain.endSeconds)
      : [];
    const worstLossSummaries = worstLoss
      ? findSummariesInRange(worstLoss.startSeconds, worstLoss.endSeconds)
      : [];

    return {
      bestGain,
      worstLoss,
      bestGainSummaries,
      worstLossSummaries,
    };
  }, [chartData, totalDuration, summaries]);

  // Track last hovered segment to avoid unnecessary updates (must be before conditional return)
  const lastHoveredSegmentRef = useRef<Summary | null>(null);
  // Track clicked segment (persists after hover ends)
  const clickedSegmentRef = useRef<Summary | null>(null);
  // Track if we're currently hovering (state to trigger re-renders)
  const [isHovering, setIsHovering] = React.useState<boolean>(false);
  // Track current hovered segment (temporary, for hover state)
  const [hoveredSegment, setHoveredSegment] = React.useState<Summary | null>(
    null,
  );

  // Compute the selected segment: use hovered segment if hovering, otherwise use clicked segment
  const selectedSegment = useMemo(() => {
    if (isHovering && hoveredSegment) {
      return hoveredSegment;
    }
    return clickedSegmentRef.current;
  }, [isHovering, hoveredSegment]);

  // Find the segment (summary) that contains a given time
  // Prefer segments where time is >= start and < end (exclusive end)
  // If time is exactly at a boundary, prefer the segment that starts at that time
  const findSegmentAtTime = (seconds: number): Summary | null => {
    if (!summaries.length) return null;

    // First, try to find segments where time is within (start, end) - exclusive end
    const exclusiveMatch = summaries.find(
      (s) => seconds >= s.startSeconds && seconds < s.endSeconds,
    );
    if (exclusiveMatch) return exclusiveMatch;

    // If no match with exclusive end, check if time is exactly at a segment start
    // In that case, prefer the segment that starts at that exact time
    const startMatch = summaries.find((s) => s.startSeconds === seconds);
    if (startMatch) return startMatch;

    // If still no match, use inclusive end (for the last segment)
    const inclusiveMatch = summaries.find(
      (s) => seconds >= s.startSeconds && seconds <= s.endSeconds,
    );
    return inclusiveMatch || null;
  };

  // Handle chart click/touch to select segment (persists)
  const handleChartInteraction = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    if (!onSegmentHover) return;

    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();

    // Get click/touch position
    let clientX: number;
    if ("touches" in e) {
      // Touch event
      const touch = e.touches[0] || e.changedTouches[0];
      if (!touch) return;
      clientX = touch.clientX;
    } else {
      // Mouse event
      clientX = e.clientX;
    }

    const x = clientX - rect.left;

    // Account for chart margins (left: 8px, right: 8px)
    const chartX = x - 8;
    const chartWidth = rect.width - 16;

    // Calculate the time in seconds based on click/touch position
    const clickedSeconds = Math.max(
      0,
      Math.min(totalDuration, (chartX / chartWidth) * totalDuration),
    );

    // Find the segment that contains this time
    const segment = findSegmentAtTime(clickedSeconds);

    // Save clicked segment (persists)
    clickedSegmentRef.current = segment;
    lastHoveredSegmentRef.current = segment;
    setHoveredSegment(null); // Clear hover state so clicked segment shows
    onSegmentHover(segment);
  };

  if (totalDuration <= 0) return null;

  const handleMarkerClick = (seconds: number) => {
    if (videoId) {
      // Subtract editOffset to account for time removed from the beginning of the YouTube video
      const adjustedSeconds = Math.max(0, Math.floor(seconds - editOffset));
      const url = `https://www.youtube.com/watch?v=${videoId}&t=${adjustedSeconds}s`;
      window.open(url, "_blank");
    }
  };

  const formatDuration = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) {
      return `${h}h ${m}m`;
    }
    return `${m}m`;
  };

  const formatTime = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    }
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const formatCeiledInt = (n: number) => Math.ceil(n).toLocaleString();

  const formatNumber = (num: number): string => {
    const n = Math.ceil(num);
    if (n >= 1000) {
      return `${Math.ceil(n / 1000)}K`;
    }
    return n.toString();
  };

  // Custom tooltip for the chart (hover only, doesn't change selection)
  const CustomTooltip = ({ active, payload }: any) => {
    // Update hover state immediately
    const currentlyHovering = active && payload && payload.length > 0;

    // Update hover state (triggers re-render)
    if (currentlyHovering !== isHovering) {
      setIsHovering(currentlyHovering);
    }

    // When not hovering, clear hovered segment so clicked segment shows
    if (!currentlyHovering) {
      if (hoveredSegment !== null) {
        setHoveredSegment(null);
      }
      // Restore clicked segment callback if needed
      if (
        onSegmentHover &&
        clickedSegmentRef.current !== null &&
        lastHoveredSegmentRef.current !== clickedSegmentRef.current
      ) {
        lastHoveredSegmentRef.current = clickedSegmentRef.current;
        requestAnimationFrame(() => {
          onSegmentHover(clickedSegmentRef.current);
        });
      }
      return null;
    }

    if (!payload || !payload.length) {
      return null;
    }

    const data = payload[0]?.payload;
    if (!data) return null;

    // Find the timeline segment that contains this time to get the correct viewer/likes values
    // Use the same boundary logic as findSegmentAtTime for consistency
    const timelineSegment =
      timelineSegments.find(
        (ts) =>
          data.offsetSeconds >= ts.startSeconds &&
          data.offsetSeconds < ts.endSeconds,
      ) ||
      timelineSegments.find(
        (ts) =>
          data.offsetSeconds >= ts.startSeconds &&
          data.offsetSeconds <= ts.endSeconds,
      );

    // On hover, temporarily show the hovered segment (but don't save it)
    // Use the timeline segment boundaries to find the matching summary segment
    const segment = timelineSegment
      ? summaries.find(
          (s) =>
            s.startSeconds === timelineSegment.startSeconds &&
            s.endSeconds === timelineSegment.endSeconds,
        ) || findSegmentAtTime(data.offsetSeconds)
      : findSegmentAtTime(data.offsetSeconds);

    // Update hoveredSegment for visual highlighting (circles) during hover
    if (segment) {
      if (segment !== hoveredSegment) {
        setHoveredSegment(segment);
      }
      if (onSegmentHover && segment !== lastHoveredSegmentRef.current) {
        lastHoveredSegmentRef.current = segment;
        // Use requestAnimationFrame to avoid calling during render
        requestAnimationFrame(() => {
          if (isHovering) {
            onSegmentHover(segment);
          }
        });
      }
    }

    // Use segment values if available, otherwise use point-in-time values
    const displayViewers = timelineSegment?.viewers ?? data.viewers;

    // Find if there's a brand mention near this time
    const nearbyMention = brandMentions.find(
      (m) => Math.abs(m.mentionStartTime - data.offsetSeconds) < 60,
    );

    return (
      <Box
        sx={{
          p: 1.5,
          maxWidth: 260,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          boxShadow: theme.shadows[8],
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            color: "text.primary",
            mb: 0.5,
          }}
        >
          {formatTime(data.offsetSeconds)}
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Typography
            sx={{ fontSize: "0.8rem", color: "text.primary", fontWeight: 600 }}
          >
            👁 {displayViewers != null ? formatCeiledInt(displayViewers) : ""}
          </Typography>
        </Box>
        {nearbyMention && (
          <Box
            sx={{
              mt: 1,
              pt: 1,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "text.primary",
                fontWeight: 600,
              }}
            >
              {nearbyMention.brandName}
            </Typography>
            {nearbyMention.context && (
              <Typography
                sx={{ fontSize: "0.65rem", color: "text.secondary", mt: 0.25 }}
              >
                "{nearbyMention.context.slice(0, 60)}..."
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const hasChartData = chartData.length > 0;

  const handleFullscreenToggle = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <>
      {!isFullscreen && (
        <Box sx={{ mb: 3 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.9rem",
                fontWeight: 700,
                color: "text.primary",
                letterSpacing: "0.05em",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              Livestream
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "text.secondary",
                    fontFamily: "monospace",
                  }}
                >
                  0:00
                </Typography>
                <Typography
                  sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                >
                  →
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "text.secondary",
                    fontFamily: "monospace",
                  }}
                >
                  {formatDuration(totalDuration)}
                </Typography>
              </Box>
              <Tooltip title="Pantalla completa">
                <IconButton
                  onClick={handleFullscreenToggle}
                  size="small"
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.text.secondary, 0.12),
                    },
                  }}
                >
                  <FullscreenIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Combined Chart + Timeline Bar */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                position: "relative",
                height: hasChartData ? 160 : 40,
                bgcolor: chartPlotBg,
                borderRadius: 3,
                overflow: "visible",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {/* Background gradient */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background: chartGradientOverlay,
                  borderRadius: 3,
                  zIndex: 0,
                }}
              />

              {/* Fallback markers when no chart data */}
              {!hasChartData && brandMentions.length > 0 && (
                <Box sx={{ position: "absolute", inset: 0, zIndex: 2 }}>
                  {brandMentions.map((mention, idx) => {
                    const position =
                      (mention.mentionStartTime / totalDuration) * 100;
                    const logoUrl = getBrandLogoUrl({
                      s3Key: mention.s3Key,
                      logoUrl: mention.logoUrl,
                    });

                    // Format mention type for display
                    const mentionTypeLabel =
                      mention.mentionType === "paid"
                        ? "💰 Patrocinio"
                        : mention.mentionType === "organic"
                          ? "🗣️ Orgánico"
                          : "⚪ Neutral";

                    return (
                      <Tooltip
                        key={`fallback-tooltip-${idx}`}
                        slotProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "background.paper",
                              color: "text.primary",
                              border: "1px solid",
                              borderColor: "divider",
                              boxShadow: theme.shadows[8],
                              maxWidth: 280,
                            },
                          },
                        }}
                        title={
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.85rem",
                                color: "text.primary",
                                mb: 0.5,
                              }}
                            >
                              {mention.brandName}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: "text.secondary",
                                mb: 0.5,
                              }}
                            >
                              {formatTime(mention.mentionStartTime)}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: "text.secondary",
                                mb: mention.context ? 0.5 : 0,
                              }}
                            >
                              {mentionTypeLabel}
                            </Typography>
                            {mention.context && (
                              <Typography
                                sx={{
                                  fontSize: "0.7rem",
                                  color: "text.secondary",
                                  mt: 0.5,
                                  fontStyle: "italic",
                                }}
                              >
                                "{mention.context.slice(0, 100)}
                                {mention.context.length > 100 ? "..." : ""}"
                              </Typography>
                            )}
                            {videoId && (
                              <Typography
                                sx={{
                                  fontSize: "0.65rem",
                                  color: "text.disabled",
                                  mt: 0.5,
                                }}
                              >
                                Haz clic para ver en YouTube
                              </Typography>
                            )}
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <Avatar
                          key={idx}
                          src={logoUrl}
                          alt={mention.brandName}
                          onClick={() =>
                            handleMarkerClick(mention.mentionStartTime)
                          }
                          sx={{
                            position: "absolute",
                            left: `calc(${Math.min(Math.max(position, 2), 98)}%)`,
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            width: { xs: 20, sm: 24, md: 28 },
                            height: { xs: 20, sm: 24, md: 28 },
                            cursor: videoId ? "pointer" : "default",
                            zIndex: 2,
                            bgcolor: logoUrl ? "transparent" : "text.secondary",
                            fontSize: {
                              xs: "0.7rem",
                              sm: "0.75rem",
                              md: "0.85rem",
                            },
                            fontWeight: 600,
                            color: "primary.contrastText",
                            "&:hover": {
                              transform: "translate(-50%, -50%) scale(1.1)",
                              zIndex: 3,
                            },
                          }}
                        >
                          {mention.brandName.charAt(0).toUpperCase()}
                        </Avatar>
                      </Tooltip>
                    );
                  })}
                </Box>
              )}

              {/* Segment tooltips overlay - positioned over highlighted areas */}
              {hasChartData && 1 < 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    bottom: 0,
                    left: 8,
                    right: 8,
                    zIndex: 3,
                    pointerEvents: "none",
                  }}
                >
                  {/* Best growth segment tooltip overlay */}
                  {viewerChangeSegments.bestGain && (
                    <Tooltip
                      title={
                        <Box sx={{ maxWidth: 300 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.8rem",
                              color: "#4ade80",
                            }}
                          >
                            📈 Mejor Crecimiento (10 min)
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                            {formatTime(
                              viewerChangeSegments.bestGain.startSeconds,
                            )}{" "}
                            →{" "}
                            {formatTime(
                              viewerChangeSegments.bestGain.endSeconds,
                            )}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              color: "#4ade80",
                              mt: 0.5,
                            }}
                          >
                            +
                            {formatCeiledInt(
                              viewerChangeSegments.bestGain.change,
                            )}{" "}
                            espectadores
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.7rem", color: "grey.400" }}
                          >
                            (
                            {formatCeiledInt(
                              viewerChangeSegments.bestGain.startViewers,
                            )}{" "}
                            →{" "}
                            {formatCeiledInt(
                              viewerChangeSegments.bestGain.endViewers,
                            )}
                            )
                          </Typography>
                          {viewerChangeSegments.bestGainSummaries.length >
                            0 && (
                            <Box
                              sx={{
                                mt: 1,
                                pt: 1,
                                borderTop: "1px solid rgba(255,255,255,0.2)",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  color: "grey.300",
                                  mb: 0.5,
                                }}
                              >
                                Qué pasó:
                              </Typography>
                              {viewerChangeSegments.bestGainSummaries
                                .slice(0, 2)
                                .map((s, idx) => (
                                  <Typography
                                    key={idx}
                                    sx={{
                                      fontSize: "0.7rem",
                                      color: "grey.400",
                                      mb: 0.25,
                                    }}
                                  >
                                    • {s.summary}
                                  </Typography>
                                ))}
                            </Box>
                          )}
                          {videoId && (
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: "primary.main",
                                mt: 0.5,
                              }}
                            >
                              Haz clic para ver →
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        onClick={() =>
                          handleMarkerClick(
                            viewerChangeSegments.bestGain!.startSeconds,
                          )
                        }
                        sx={{
                          position: "absolute",
                          left: `${(viewerChangeSegments.bestGain.startSeconds / totalDuration) * 100}%`,
                          width: `${((viewerChangeSegments.bestGain.endSeconds - viewerChangeSegments.bestGain.startSeconds) / totalDuration) * 100}%`,
                          top: 0,
                          bottom: 0,
                          cursor: videoId ? "pointer" : "default",
                          pointerEvents: "auto",
                          "&:hover": {
                            bgcolor: "rgba(34,197,94,0.15)",
                          },
                        }}
                      />
                    </Tooltip>
                  )}

                  {/* Worst loss segment tooltip overlay */}
                  {viewerChangeSegments.worstLoss && (
                    <Tooltip
                      title={
                        <Box sx={{ maxWidth: 300 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.8rem",
                              color: "#f87171",
                            }}
                          >
                            📉 Mayor Caída (10 min)
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                            {formatTime(
                              viewerChangeSegments.worstLoss.startSeconds,
                            )}{" "}
                            →{" "}
                            {formatTime(
                              viewerChangeSegments.worstLoss.endSeconds,
                            )}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              color: "#f87171",
                              mt: 0.5,
                            }}
                          >
                            {formatCeiledInt(
                              viewerChangeSegments.worstLoss.change,
                            )}{" "}
                            espectadores
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.7rem", color: "grey.400" }}
                          >
                            (
                            {formatCeiledInt(
                              viewerChangeSegments.worstLoss.startViewers,
                            )}{" "}
                            →{" "}
                            {formatCeiledInt(
                              viewerChangeSegments.worstLoss.endViewers,
                            )}
                            )
                          </Typography>
                          {viewerChangeSegments.worstLossSummaries.length >
                            0 && (
                            <Box
                              sx={{
                                mt: 1,
                                pt: 1,
                                borderTop: "1px solid rgba(255,255,255,0.2)",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  color: "grey.300",
                                  mb: 0.5,
                                }}
                              >
                                Qué pasó:
                              </Typography>
                              {viewerChangeSegments.worstLossSummaries
                                .slice(0, 2)
                                .map((s, idx) => (
                                  <Typography
                                    key={idx}
                                    sx={{
                                      fontSize: "0.7rem",
                                      color: "grey.400",
                                      mb: 0.25,
                                    }}
                                  >
                                    • {s.summary}
                                  </Typography>
                                ))}
                            </Box>
                          )}
                          {videoId && (
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: "primary.main",
                                mt: 0.5,
                              }}
                            >
                              Haz clic para ver →
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        onClick={() =>
                          handleMarkerClick(
                            viewerChangeSegments.worstLoss!.startSeconds,
                          )
                        }
                        sx={{
                          position: "absolute",
                          left: `${(viewerChangeSegments.worstLoss.startSeconds / totalDuration) * 100}%`,
                          width: `${((viewerChangeSegments.worstLoss.endSeconds - viewerChangeSegments.worstLoss.startSeconds) / totalDuration) * 100}%`,
                          top: 0,
                          bottom: 0,
                          cursor: videoId ? "pointer" : "default",
                          pointerEvents: "auto",
                          "&:hover": {
                            bgcolor: "rgba(239,68,68,0.15)",
                          },
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              )}

              {/* Area Chart (if we have data) */}
              {hasChartData && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 2,
                    cursor: "pointer",
                  }}
                  onClick={handleChartInteraction}
                  onTouchEnd={handleChartInteraction}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={chartData}
                      margin={{ top: 8, right: 20, bottom: 0, left: 20 }}
                    >
                      <defs>
                        <linearGradient
                          id="viewerGradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor={chartLine}
                            stopOpacity={0.35}
                          />
                          <stop
                            offset="100%"
                            stopColor={chartLine}
                            stopOpacity={0.06}
                          />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="offsetSeconds"
                        hide
                        domain={[0, totalDuration]}
                        type="number"
                      />
                      <YAxis
                        yAxisId="viewers"
                        domain={[0, "dataMax"]}
                        tickFormatter={formatNumber}
                        tick={{ fontSize: 10, fill: chartAxisTickFill }}
                        width={35}
                      />
                      <ChartTooltip content={<CustomTooltip />} />

                      {/* Area charts - rendered first so dots appear on top */}
                      <Area
                        type="monotone"
                        dataKey="viewers"
                        stroke={chartLine}
                        strokeWidth={1.5}
                        fill="url(#viewerGradient)"
                        yAxisId="viewers"
                      />
                      {/* Highlight selected segment (behind markers) */}
                      {selectedSegment && (
                        <>
                          <ReferenceArea
                            x1={selectedSegment.startSeconds}
                            x2={selectedSegment.endSeconds}
                            fill={chartLine}
                            fillOpacity={0.12}
                            stroke="none"
                          />
                          {/* Persistent dots on lines for selected segment */}
                          {(() => {
                            // Find the data point closest to the segment start time
                            const segmentMidpoint =
                              (selectedSegment.startSeconds +
                                selectedSegment.endSeconds) /
                              2;
                            const closestPoint = chartData.reduce(
                              (prev, curr) =>
                                Math.abs(curr.offsetSeconds - segmentMidpoint) <
                                Math.abs(prev.offsetSeconds - segmentMidpoint)
                                  ? curr
                                  : prev,
                              chartData[0],
                            );

                            if (!closestPoint) return null;

                            return (
                              <>
                                <ReferenceDot
                                  x={closestPoint.offsetSeconds}
                                  y={closestPoint.viewers}
                                  r={5}
                                  fill={chartLine}
                                  stroke={dotStrokeColor}
                                  strokeWidth={2}
                                  yAxisId="viewers"
                                />
                              </>
                            );
                          })()}
                        </>
                      )}

                      {/* Brand mention reference lines and dots */}
                      {brandMentions.map((mention, idx) => {
                        const color = mentionLineColor;
                        // Find closest data point to get viewer count at this time
                        const closestPoint = chartData.reduce(
                          (prev, curr) =>
                            Math.abs(
                              curr.offsetSeconds - mention.mentionStartTime,
                            ) <
                            Math.abs(
                              prev.offsetSeconds - mention.mentionStartTime,
                            )
                              ? curr
                              : prev,
                          chartData[0],
                        );
                        const logoUrl = getBrandLogoUrl({
                          s3Key: mention.s3Key,
                          logoUrl: mention.logoUrl,
                        });
                        const mentionTypeLabel =
                          mention.mentionType === "paid"
                            ? "💰 Patrocinio"
                            : mention.mentionType === "organic"
                              ? "🗣️ Orgánico"
                              : "⚪ Neutral";

                        return (
                          <React.Fragment key={`mention-${idx}`}>
                            <ReferenceLine
                              x={mention.mentionStartTime}
                              stroke={color}
                              strokeWidth={2}
                              strokeDasharray="4 2"
                              yAxisId="viewers"
                            />
                            {closestPoint && (
                              <ReferenceDot
                                x={mention.mentionStartTime}
                                y={closestPoint.viewers}
                                yAxisId="viewers"
                                r={14}
                                fill="transparent"
                                stroke="none"
                                style={{
                                  cursor: videoId ? "pointer" : "default",
                                }}
                                onClick={() =>
                                  handleMarkerClick(mention.mentionStartTime)
                                }
                                shape={(props: any) => {
                                  const { cx, cy } = props;
                                  const size = 28;
                                  return (
                                    <g
                                      style={{
                                        cursor: videoId ? "pointer" : "default",
                                      }}
                                      onClick={() =>
                                        handleMarkerClick(
                                          mention.mentionStartTime,
                                        )
                                      }
                                    >
                                      <defs>
                                        <clipPath id={`clip-brand-${idx}`}>
                                          <circle
                                            cx={cx}
                                            cy={cy}
                                            r={size / 2}
                                          />
                                        </clipPath>
                                      </defs>
                                      {logoUrl ? (
                                        <image
                                          href={logoUrl}
                                          x={cx - size / 2}
                                          y={cy - size / 2}
                                          width={size}
                                          height={size}
                                          clipPath={`url(#clip-brand-${idx})`}
                                          preserveAspectRatio="xMidYMid slice"
                                        />
                                      ) : (
                                        <>
                                          <circle
                                            cx={cx}
                                            cy={cy}
                                            r={size / 2}
                                            fill={color}
                                          />
                                          <text
                                            x={cx}
                                            y={cy}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            fill={svgMutedTextFill}
                                            fontSize={12}
                                            fontWeight={600}
                                          >
                                            {mention.brandName
                                              .charAt(0)
                                              .toUpperCase()}
                                          </text>
                                        </>
                                      )}
                                      <title>{`${mention.brandName} - ${formatTime(mention.mentionStartTime)} - ${mentionTypeLabel}`}</title>
                                    </g>
                                  );
                                }}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Box>

            {/* Legend */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: { xs: 1.5, sm: 2.5 },
                mt: 1.5,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {hasChartData && (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 14,
                        height: 6,
                        bgcolor: legendViewerSwatchBg,
                        borderRadius: 0.5,
                        border: "1px solid",
                        borderColor: "primary.main",
                      }}
                    />
                    <Typography
                      sx={{ fontSize: "0.6rem", color: "text.secondary" }}
                    >
                      Espectadores
                    </Typography>
                  </Box>
                </>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    bgcolor: "text.secondary",
                    borderRadius: "50%",
                    border: "2px solid",
                    borderColor: "background.paper",
                    boxShadow: `0 0 4px ${alpha(theme.palette.text.secondary, 0.4)}`,
                  }}
                />
                <Typography
                  sx={{ fontSize: "0.6rem", color: "text.secondary" }}
                >
                  Patrocinador
                </Typography>
              </Box>
              {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 14, height: 6, bgcolor: 'rgba(34,197,94,0.15)', borderRadius: 0.5, border: '1px solid #22c55e' }} />
                                <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                                    Mejor Crecimiento
                                    {viewerChangeSegments.bestGain && (
                                        <span style={{ color: '#22c55e', fontWeight: 600 }}>
                                            {' '}+{formatCeiledInt(viewerChangeSegments.bestGain.change)}
                                        </span>
                                    )}
                                </Typography>
                            </Box> */}
              {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 14, height: 6, bgcolor: 'rgba(239,68,68,0.15)', borderRadius: 0.5, border: '1px solid #ef4444' }} />
                                <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                                    Mayor Caída
                                    {viewerChangeSegments.worstLoss && (
                                        <span style={{ color: '#ef4444', fontWeight: 600 }}>
                                            {' '}{formatCeiledInt(viewerChangeSegments.worstLoss.change)}
                                        </span>
                                    )}
                                </Typography>
                            </Box> */}
            </Box>

            {/* Espectadores promedio / peak / chat stats pills below chart */}
            {(averageViewers != null ||
              peakViewers != null ||
              chatMessageCount != null ||
              uniqueChatUsers != null) && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr 1fr",
                    sm: "1fr 1fr",
                    md: "1fr 1fr 1fr 1fr",
                  },
                  gap: 2,
                  mt: 2,
                }}
              >
                {averageViewers != null && (
                  <LivestreamStatPill
                    label={getMetricLabel("espectadoresEnVivoPromedio", "long")}
                    tooltip={getMetricTooltip("espectadoresEnVivoPromedio")}
                    formattedValue={formatCeiledInt(averageViewers)}
                    bgcolor={statPillBg}
                  />
                )}
                {peakViewers != null && (
                  <LivestreamStatPill
                    label={getMetricLabel("espectadoresEnVivoPeak", "long")}
                    tooltip={getMetricTooltip("espectadoresEnVivoPeak")}
                    formattedValue={formatCeiledInt(peakViewers)}
                    bgcolor={statPillBg}
                  />
                )}
                {chatMessageCount != null && chatMessageCount > 0 && (
                  <LivestreamStatPill
                    label="Mensajes de chat"
                    tooltip="Mensajes de chat"
                    formattedValue={formatCeiledInt(chatMessageCount)}
                    bgcolor={statPillBg}
                  />
                )}
                {uniqueChatUsers != null && uniqueChatUsers > 0 && (
                  <LivestreamStatPill
                    label="Usuarios en chat"
                    tooltip="Usuarios en chat"
                    formattedValue={formatCeiledInt(uniqueChatUsers)}
                    bgcolor={statPillBg}
                  />
                )}
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Fullscreen Dialog - Only render when open */}
      {isFullscreen && (
        <Dialog
          open={isFullscreen}
          onClose={handleFullscreenToggle}
          fullScreen
          PaperProps={{
            sx: {
              bgcolor: "background.paper",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 700,
                  color: "text.primary",
                  letterSpacing: "0.05em",
                }}
              >
                Livestream
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: "text.secondary",
                      fontFamily: "monospace",
                    }}
                  >
                    0:00
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.8rem", color: "text.secondary" }}
                  >
                    →
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: "text.secondary",
                      fontFamily: "monospace",
                    }}
                  >
                    {formatDuration(totalDuration)}
                  </Typography>
                </Box>
                <Tooltip title="Salir de pantalla completa">
                  <IconButton
                    onClick={handleFullscreenToggle}
                    size="small"
                    sx={{
                      color: "text.secondary",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.text.secondary, 0.12),
                      },
                    }}
                  >
                    <FullscreenExitIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              p: 2,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  flex: 1,
                  minHeight: 400,
                  height: "100%",
                  bgcolor: chartPlotBg,
                  borderRadius: 3,
                  overflow: "visible",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                {/* Background gradient */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: chartGradientOverlay,
                    borderRadius: 3,
                    zIndex: 0,
                  }}
                />

                {/* Fallback markers when no chart data */}
                {!hasChartData && brandMentions.length > 0 && (
                  <Box sx={{ position: "absolute", inset: 0, zIndex: 2 }}>
                    {brandMentions.map((mention, idx) => {
                      const position =
                        (mention.mentionStartTime / totalDuration) * 100;
                      const logoUrl = getBrandLogoUrl({
                        s3Key: mention.s3Key,
                        logoUrl: mention.logoUrl,
                      });

                      const mentionTypeLabel =
                        mention.mentionType === "paid"
                          ? "💰 Patrocinio"
                          : mention.mentionType === "organic"
                            ? "🗣️ Orgánico"
                            : "⚪ Neutral";

                      return (
                        <Tooltip
                          key={`fallback-tooltip-fs-${idx}`}
                          slotProps={{
                            tooltip: {
                              sx: {
                                bgcolor: "background.paper",
                                color: "text.primary",
                                border: "1px solid",
                                borderColor: "divider",
                                boxShadow: theme.shadows[8],
                                maxWidth: 280,
                              },
                            },
                          }}
                          title={
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.85rem",
                                  color: "text.primary",
                                  mb: 0.5,
                                }}
                              >
                                {mention.brandName}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  color: "text.secondary",
                                  mb: 0.5,
                                }}
                              >
                                {formatTime(mention.mentionStartTime)}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.7rem",
                                  color: "text.secondary",
                                  mb: mention.context ? 0.5 : 0,
                                }}
                              >
                                {mentionTypeLabel}
                              </Typography>
                              {mention.context && (
                                <Typography
                                  sx={{
                                    fontSize: "0.7rem",
                                    color: "text.secondary",
                                    mt: 0.5,
                                    fontStyle: "italic",
                                  }}
                                >
                                  "{mention.context.slice(0, 100)}
                                  {mention.context.length > 100 ? "..." : ""}"
                                </Typography>
                              )}
                              {videoId && (
                                <Typography
                                  sx={{
                                    fontSize: "0.65rem",
                                    color: "text.disabled",
                                    mt: 0.5,
                                  }}
                                >
                                  Haz clic para ver en YouTube
                                </Typography>
                              )}
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <Avatar
                            src={logoUrl}
                            alt={mention.brandName}
                            onClick={() =>
                              handleMarkerClick(mention.mentionStartTime)
                            }
                            sx={{
                              position: "absolute",
                              left: `calc(${Math.min(Math.max(position, 2), 98)}%)`,
                              top: "50%",
                              transform: "translate(-50%, -50%)",
                              width: { xs: 24, sm: 28, md: 32 },
                              height: { xs: 24, sm: 28, md: 32 },
                              cursor: videoId ? "pointer" : "default",
                              zIndex: 2,
                              bgcolor: logoUrl
                                ? "transparent"
                                : "text.secondary",
                              fontSize: {
                                xs: "0.75rem",
                                sm: "0.85rem",
                                md: "0.95rem",
                              },
                              fontWeight: 600,
                              color: "primary.contrastText",
                              "&:hover": {
                                transform: "translate(-50%, -50%) scale(1.1)",
                                zIndex: 3,
                              },
                            }}
                          >
                            {mention.brandName.charAt(0).toUpperCase()}
                          </Avatar>
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}

                {/* Area Chart (if we have data) - Fullscreen version */}
                {hasChartData && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 2,
                      cursor: "pointer",
                    }}
                    onClick={handleChartInteraction}
                    onTouchEnd={handleChartInteraction}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={chartData}
                        margin={{ top: 8, right: 20, bottom: 0, left: 20 }}
                      >
                        <defs>
                          <linearGradient
                            id="viewerGradientFs"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor={chartLine}
                              stopOpacity={0.35}
                            />
                            <stop
                              offset="100%"
                              stopColor={chartLine}
                              stopOpacity={0.06}
                            />
                          </linearGradient>
                        </defs>
                        <XAxis
                          dataKey="offsetSeconds"
                          hide
                          domain={[0, totalDuration]}
                          type="number"
                        />
                        <YAxis
                          yAxisId="viewers"
                          domain={[0, "dataMax"]}
                          tickFormatter={formatNumber}
                          tick={{ fontSize: 10, fill: chartAxisTickFill }}
                          width={35}
                        />
                        <ChartTooltip content={<CustomTooltip />} />

                        <Area
                          type="monotone"
                          dataKey="viewers"
                          stroke={chartLine}
                          strokeWidth={1.5}
                          fill="url(#viewerGradientFs)"
                          yAxisId="viewers"
                        />
                        {selectedSegment && (
                          <>
                            <ReferenceArea
                              x1={selectedSegment.startSeconds}
                              x2={selectedSegment.endSeconds}
                              fill={chartLine}
                              fillOpacity={0.12}
                              stroke="none"
                            />
                            {(() => {
                              const segmentMidpoint =
                                (selectedSegment.startSeconds +
                                  selectedSegment.endSeconds) /
                                2;
                              const closestPoint = chartData.reduce(
                                (prev, curr) =>
                                  Math.abs(
                                    curr.offsetSeconds - segmentMidpoint,
                                  ) <
                                  Math.abs(prev.offsetSeconds - segmentMidpoint)
                                    ? curr
                                    : prev,
                                chartData[0],
                              );

                              if (!closestPoint) return null;

                              return (
                                <>
                                  <ReferenceDot
                                    x={closestPoint.offsetSeconds}
                                    y={closestPoint.viewers}
                                    r={5}
                                    fill={chartLine}
                                    stroke={dotStrokeColor}
                                    strokeWidth={2}
                                    yAxisId="viewers"
                                  />
                                </>
                              );
                            })()}
                          </>
                        )}

                        {/* Brand mention reference lines and dots - Fullscreen */}
                        {brandMentions.map((mention, idx) => {
                          const color = mentionLineColor;
                          const closestPoint = chartData.reduce(
                            (prev, curr) =>
                              Math.abs(
                                curr.offsetSeconds - mention.mentionStartTime,
                              ) <
                              Math.abs(
                                prev.offsetSeconds - mention.mentionStartTime,
                              )
                                ? curr
                                : prev,
                            chartData[0],
                          );
                          const logoUrl = getBrandLogoUrl({
                            s3Key: mention.s3Key,
                            logoUrl: mention.logoUrl,
                          });
                          const mentionTypeLabel =
                            mention.mentionType === "paid"
                              ? "💰 Patrocinio"
                              : mention.mentionType === "organic"
                                ? "🗣️ Orgánico"
                                : "⚪ Neutral";

                          return (
                            <React.Fragment key={`mention-fs-${idx}`}>
                              <ReferenceLine
                                x={mention.mentionStartTime}
                                stroke={color}
                                strokeWidth={2}
                                strokeDasharray="4 2"
                                yAxisId="viewers"
                              />
                              {closestPoint && (
                                <ReferenceDot
                                  x={mention.mentionStartTime}
                                  y={closestPoint.viewers}
                                  yAxisId="viewers"
                                  r={16}
                                  fill="transparent"
                                  stroke="none"
                                  style={{
                                    cursor: videoId ? "pointer" : "default",
                                  }}
                                  onClick={() =>
                                    handleMarkerClick(mention.mentionStartTime)
                                  }
                                  shape={(props: any) => {
                                    const { cx, cy } = props;
                                    const size = 32;
                                    return (
                                      <g
                                        style={{
                                          cursor: videoId
                                            ? "pointer"
                                            : "default",
                                        }}
                                        onClick={() =>
                                          handleMarkerClick(
                                            mention.mentionStartTime,
                                          )
                                        }
                                      >
                                        <defs>
                                          <clipPath id={`clip-brand-fs-${idx}`}>
                                            <circle
                                              cx={cx}
                                              cy={cy}
                                              r={size / 2}
                                            />
                                          </clipPath>
                                        </defs>
                                        {logoUrl ? (
                                          <image
                                            href={logoUrl}
                                            x={cx - size / 2}
                                            y={cy - size / 2}
                                            width={size}
                                            height={size}
                                            clipPath={`url(#clip-brand-fs-${idx})`}
                                            preserveAspectRatio="xMidYMid slice"
                                          />
                                        ) : (
                                          <>
                                            <circle
                                              cx={cx}
                                              cy={cy}
                                              r={size / 2}
                                              fill={color}
                                            />
                                            <text
                                              x={cx}
                                              y={cy}
                                              textAnchor="middle"
                                              dominantBaseline="central"
                                              fill={svgMutedTextFill}
                                              fontSize={14}
                                              fontWeight={600}
                                            >
                                              {mention.brandName
                                                .charAt(0)
                                                .toUpperCase()}
                                            </text>
                                          </>
                                        )}
                                        <title>{`${mention.brandName} - ${formatTime(mention.mentionStartTime)} - ${mentionTypeLabel}`}</title>
                                      </g>
                                    );
                                  }}
                                />
                              )}
                            </React.Fragment>
                          );
                        })}
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Box>

              {/* Legend - Fullscreen */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: { xs: 1.5, sm: 2.5 },
                  mt: 1.5,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {hasChartData && (
                  <>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 14,
                          height: 6,
                          bgcolor: legendViewerSwatchBg,
                          borderRadius: 0.5,
                          border: "1px solid",
                          borderColor: "primary.main",
                        }}
                      />
                      <Typography
                        sx={{ fontSize: "0.6rem", color: "text.secondary" }}
                      >
                        Espectadores
                      </Typography>
                    </Box>
                  </>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      bgcolor: "text.secondary",
                      borderRadius: "50%",
                      border: "2px solid",
                      borderColor: "background.paper",
                      boxShadow: `0 0 4px ${alpha(theme.palette.text.secondary, 0.4)}`,
                    }}
                  />
                  <Typography
                    sx={{ fontSize: "0.6rem", color: "text.secondary" }}
                  >
                    Patrocinador
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 14,
                      height: 6,
                      bgcolor: "rgba(34,197,94,0.15)",
                      borderRadius: 0.5,
                      border: "1px solid #22c55e",
                    }}
                  />
                  <Typography
                    sx={{ fontSize: "0.6rem", color: "text.secondary" }}
                  >
                    Mejor Crecimiento
                    {viewerChangeSegments.bestGain && (
                      <span style={{ color: "#22c55e", fontWeight: 600 }}>
                        {" "}
                        +{formatCeiledInt(viewerChangeSegments.bestGain.change)}
                      </span>
                    )}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 14,
                      height: 6,
                      bgcolor: "rgba(239,68,68,0.15)",
                      borderRadius: 0.5,
                      border: "1px solid #ef4444",
                    }}
                  />
                  <Typography
                    sx={{ fontSize: "0.6rem", color: "text.secondary" }}
                  >
                    Mayor Caída
                    {viewerChangeSegments.worstLoss && (
                      <span style={{ color: "#ef4444", fontWeight: 600 }}>
                        {" "}
                        {formatCeiledInt(viewerChangeSegments.worstLoss.change)}
                      </span>
                    )}
                  </Typography>
                </Box>
              </Box>

              {/* Espectadores promedio / peak / chat stats pills - Fullscreen */}
              {(averageViewers != null ||
                peakViewers != null ||
                chatMessageCount != null ||
                uniqueChatUsers != null) && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr 1fr",
                      sm: "1fr 1fr",
                      md: "1fr 1fr 1fr 1fr",
                    },
                    gap: 2,
                    mt: 2,
                  }}
                >
                  {averageViewers != null && (
                    <LivestreamStatPill
                      label={getMetricLabel("espectadoresEnVivoPromedio", "long")}
                      tooltip={getMetricTooltip("espectadoresEnVivoPromedio")}
                      formattedValue={formatCeiledInt(averageViewers)}
                      bgcolor={statPillBg}
                    />
                  )}
                  {peakViewers != null && (
                    <LivestreamStatPill
                      label={getMetricLabel("espectadoresEnVivoPeak", "long")}
                      tooltip={getMetricTooltip("espectadoresEnVivoPeak")}
                      formattedValue={formatCeiledInt(peakViewers)}
                      bgcolor={statPillBg}
                    />
                  )}
                  {chatMessageCount != null && chatMessageCount > 0 && (
                    <LivestreamStatPill
                      label="Mensajes de chat"
                      tooltip="Mensajes de chat"
                      formattedValue={formatCeiledInt(chatMessageCount)}
                      bgcolor={statPillBg}
                    />
                  )}
                  {uniqueChatUsers != null && uniqueChatUsers > 0 && (
                    <LivestreamStatPill
                      label="Usuarios en chat"
                      tooltip="Usuarios en chat"
                      formattedValue={formatCeiledInt(uniqueChatUsers)}
                      bgcolor={statPillBg}
                    />
                  )}
                </Box>
              )}
            </Paper>
          </Box>
        </Dialog>
      )}
    </>
  );
};

export default StoryTimelineBar;
